import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, GraduationCap, Globe2, Sparkles } from "lucide-react";
import { fetchTours, submitInquiry } from "@/lib/api";
import { QUOTE } from "@/constants/testIds";

const STEPS = [
  { n: "01", label: "Trip Type" },
  { n: "02", label: "Your Group" },
  { n: "03", label: "Contact" },
];

const EMPTY = {
  name: "", email: "", phone: "", organization: "", role: "",
  trip_type: "", tour_slug: "", destination_interest: "", group_size: 25,
  travel_window: "", duration_days: "", budget_range: "", message: "",
};

export default function RequestQuote() {
  const [params] = useSearchParams();
  const preTour = params.get("tour") || "";
  const [tours, setTours] = useState([]);
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ ...EMPTY, tour_slug: preTour });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    fetchTours().then(setTours).catch(() => setTours([]));
  }, []);

  useEffect(() => {
    if (preTour && tours.length) {
      const t = tours.find((x) => x.slug === preTour);
      if (t) {
        setForm((f) => ({
          ...f,
          tour_slug: preTour,
          trip_type: t.category === "Group Travel" ? "Group Travel" : "College Tour",
        }));
      }
    }
  }, [preTour, tours]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validateStep = () => {
    const e = {};
    if (step === 1 && !form.trip_type) e.trip_type = "Pick a trip type";
    if (step === 2) {
      if (!form.organization || form.organization.length < 2) e.organization = "Required";
      if (!form.role) e.role = "Required";
      if (!form.group_size || Number(form.group_size) < 25) e.group_size = "Groups of 25 or more only";
      if (!form.destination_interest && !form.tour_slug) e.destination_interest = "Tell us where you'd like to go";
      if (!form.travel_window) e.travel_window = "Required";
    }
    if (step === 3) {
      if (!form.name || form.name.length < 2) e.name = "Required";
      if (!form.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Valid email required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validateStep()) setStep((s) => Math.min(3, s + 1)); };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async (e) => {
    e.preventDefault();
    if (!validateStep()) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        group_size: Number(form.group_size),
        duration_days: form.duration_days ? Number(form.duration_days) : null,
        tour_slug: form.tour_slug || null,
      };
      Object.keys(payload).forEach((k) => {
        if (payload[k] === "" || payload[k] === null || payload[k] === undefined) delete payload[k];
      });
      payload.group_size = Number(form.group_size);
      await submitInquiry(payload);
      toast.success("Inquiry received! We'll be in touch within 2 business days.");
      setSubmitted(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <section className="container-edge py-28 max-w-2xl" data-testid={QUOTE.successPanel}>
        <div className="bg-white rounded-[2rem] p-12 ring-1 ring-ink/8 soft-shadow text-center">
          <div className="w-20 h-20 rounded-full bg-coral/15 mx-auto flex items-center justify-center mb-6">
            <Check size={36} className="text-coral" strokeWidth={2} />
          </div>
          <div className="eyebrow justify-center mb-3">Inquiry Received</div>
          <h1 className="display-serif text-4xl mb-5 leading-tight">
            Thank you! <span className="hand-accent text-coral">We've got you.</span>
          </h1>
          <p className="text-muted2 leading-relaxed mb-8">
            A KNV trip coordinator will reach out within two business days with first questions and a draft route.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/tours" className="btn-secondary">Browse more tours</Link>
            <Link to="/" className="btn-ghost">Back to home</Link>
          </div>
        </div>
      </section>
    );
  }

  const filteredTours = tours.filter((t) => {
    if (!form.trip_type) return true;
    if (form.trip_type === "College Tour") return ["College Tour", "HBCU Tour", "Day Tour"].includes(t.category);
    return t.category === "Group Travel";
  });

  return (
    <section className="container-edge py-20 max-w-3xl" data-testid={QUOTE.formRoot}>
      <div className="eyebrow mb-3"><Sparkles size={14} className="fill-coral" /> Plan a Trip</div>
      <h1 className="display-serif text-5xl leading-[1.05] mb-5">
        Tell us about your group. <span className="hand-accent text-coral">We'll handle the rest.</span>
      </h1>
      <p className="text-muted2 leading-relaxed mb-10 max-w-xl">
        No bookings online — this is the start of a real conversation. Share the basics and one of our trip
        coordinators will respond within two business days.
      </p>

      <div className="flex items-center gap-3 mb-12 overflow-x-auto pb-2">
        {STEPS.map((s, i) => {
          const idx = i + 1;
          const active = step === idx;
          const done = step > idx;
          return (
            <div key={s.n} className={`flex items-center gap-3 px-4 py-2 rounded-full ${active ? "bg-coral/15" : done ? "bg-teal/15" : "bg-ink/5"}`}>
              <span className={`font-mono text-[10px] tracking-[0.2em] ${active ? "text-coralDeep" : done ? "text-teal" : "text-ink/40"}`}>
                {s.n}
              </span>
              <span className={`text-sm whitespace-nowrap ${active ? "text-ink font-semibold" : done ? "text-teal" : "text-ink/45"}`}>
                {s.label}
              </span>
            </div>
          );
        })}
      </div>

      <form onSubmit={submit} className="space-y-8">
        {step === 1 && (
          <div className="animate-fade-up" data-testid={QUOTE.step1}>
            <label className="field-label">What kind of trip are you planning?</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
              <TripTypeCard
                testId={QUOTE.tripTypeCollege}
                active={form.trip_type === "College Tour"}
                onClick={() => set("trip_type", "College Tour")}
                icon={GraduationCap}
                title="College Tour"
                desc="HBCU tours, mainstream colleges, or a one-day tour for groups not ready for overnight travel."
              />
              <TripTypeCard
                testId={QUOTE.tripTypeGroup}
                active={form.trip_type === "Group Travel"}
                onClick={() => set("trip_type", "Group Travel")}
                icon={Globe2}
                title="Group Travel"
                desc="A dream destination trip for your group of 25+. Domestic or international — we plan it all."
              />
            </div>
            {errors.trip_type && <div className="mt-3 text-xs text-red-700 font-mono uppercase tracking-[0.15em]">{errors.trip_type}</div>}
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6 animate-fade-up" data-testid={QUOTE.step2}>
            <Field label="Organization / School" error={errors.organization}>
              <input data-testid={QUOTE.org} className="editorial-input" value={form.organization}
                onChange={(e) => set("organization", e.target.value)} placeholder="e.g., Lincoln Prep Academy" />
            </Field>
            <Field label="Your role" error={errors.role}>
              <input data-testid={QUOTE.role} className="editorial-input" value={form.role}
                onChange={(e) => set("role", e.target.value)} placeholder="Teacher · Counselor · Group leader" />
            </Field>
            <Field label="Group size (25 minimum)" error={errors.group_size}>
              <input type="number" min="25" max="2000" data-testid={QUOTE.groupSize} className="editorial-input"
                value={form.group_size} onChange={(e) => set("group_size", e.target.value)} />
            </Field>
            <Field label="Interested in a specific tour? (optional)">
              <select data-testid={QUOTE.tourSelect} className="editorial-input cursor-pointer"
                value={form.tour_slug} onChange={(e) => set("tour_slug", e.target.value)}>
                <option value="">— No specific tour yet —</option>
                {filteredTours.map((t) => (<option key={t.slug} value={t.slug}>{t.title}</option>))}
              </select>
            </Field>
            <Field label="Destination / theme interest" error={errors.destination_interest}>
              <input data-testid={QUOTE.destination} className="editorial-input" value={form.destination_interest}
                onChange={(e) => set("destination_interest", e.target.value)}
                placeholder="HBCUs · D.C. civics · Europe · Caribbean…" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Travel window" error={errors.travel_window}>
                <input data-testid={QUOTE.window} className="editorial-input" value={form.travel_window}
                  onChange={(e) => set("travel_window", e.target.value)} placeholder="e.g., Spring Break 2026" />
              </Field>
              <Field label="Duration (days)">
                <input type="number" min="1" max="60" data-testid={QUOTE.duration} className="editorial-input"
                  value={form.duration_days} onChange={(e) => set("duration_days", e.target.value)} placeholder="5" />
              </Field>
            </div>
            <Field label="Budget range (optional)">
              <input data-testid={QUOTE.budget} className="editorial-input" value={form.budget_range}
                onChange={(e) => set("budget_range", e.target.value)} placeholder="$1,200–$1,800 per traveler" />
            </Field>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6 animate-fade-up" data-testid={QUOTE.step3}>
            <Field label="Your name" error={errors.name}>
              <input data-testid={QUOTE.name} className="editorial-input" value={form.name}
                onChange={(e) => set("name", e.target.value)} placeholder="First and last" />
            </Field>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Email" error={errors.email}>
                <input type="email" data-testid={QUOTE.email} className="editorial-input" value={form.email}
                  onChange={(e) => set("email", e.target.value)} placeholder="you@school.edu" />
              </Field>
              <Field label="Phone (optional)">
                <input data-testid={QUOTE.phone} className="editorial-input" value={form.phone}
                  onChange={(e) => set("phone", e.target.value)} placeholder="(555) 555-1212" />
              </Field>
            </div>
            <Field label="Anything else we should know? (optional)">
              <textarea data-testid={QUOTE.message} className="editorial-input resize-none min-h-[120px]"
                value={form.message} onChange={(e) => set("message", e.target.value)}
                placeholder="Accessibility needs, dietary, learning goals, must-include stops…" />
            </Field>
          </div>
        )}

        <div className="flex items-center justify-between pt-6 border-t border-ink/10">
          {step > 1 ? (
            <button type="button" onClick={back} className="btn-ghost" data-testid={QUOTE.prevBtn}>
              <ArrowLeft size={14} /> Back
            </button>
          ) : <span />}
          {step < 3 ? (
            <button type="button" onClick={next} className="btn-primary" data-testid={QUOTE.nextBtn}>
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button type="submit" disabled={submitting} className="btn-primary disabled:opacity-60" data-testid={QUOTE.submitBtn}>
              {submitting ? "Sending…" : <>Send Inquiry <Check size={16} /></>}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}

function Field({ label, error, children }) {
  return (
    <div>
      <label className="field-label">{label}</label>
      {children}
      {error && <div className="mt-2 text-xs text-red-700 font-mono uppercase tracking-[0.15em]">{error}</div>}
    </div>
  );
}

function TripTypeCard({ active, onClick, icon: Icon, title, desc, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`text-left p-6 rounded-3xl border-2 transition-all hover:-translate-y-0.5 ${
        active ? "border-coral bg-coral/8 ring-4 ring-coral/15" : "border-ink/10 bg-white hover:border-ink/25"
      }`}
    >
      <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-4 ${active ? "bg-coral text-cream" : "bg-ink/5 text-ink"}`}>
        <Icon size={22} />
      </div>
      <h3 className="display-serif text-xl mb-1.5">{title}</h3>
      <p className="text-sm text-muted2 leading-relaxed">{desc}</p>
    </button>
  );
}
